import type { DataGlueGraph } from '@glueapp/graph-types';
import type { ValidationResult } from '@glueapp/graph-validation';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuthenticatedAxiosInstance } from '../api/useAuthenticatedAxiosInstance';
import { getEnvironmentConfig } from '../environmentConfig';

export type ExecutionResult = { [key: number]: { output: { [key: string]: any }; executionDecisions?: { [key: string]: boolean } } };
export type ExecutionError = { nodeInstanceId?: number; error?: string; message?: string };

export type NodeRunRecordDictionary = {
    [key: number]: Set<number>;
};

export const useGraphExecution = (
    graphId: string,
    graph: DataGlueGraph | undefined,
    validationResult: ValidationResult | undefined,
    graphValid: boolean,
    flushSave?: () => Promise<void>,
) => {
    const [executionResult, setExecutionResult] = useState<ExecutionResult | undefined>(undefined);
    const [executionError, setExecutionError] = useState<ExecutionError | undefined>(undefined);
    const [executionRunning, setExecutionRunning] = useState<boolean>(false);

    const api = useAuthenticatedAxiosInstance(getEnvironmentConfig().graphServiceUrl);

    const resetExecution = () => {
        setExecutionResult(undefined);
        setExecutionError(undefined);
    };

    const [ranNodes] = useState<NodeRunRecordDictionary>({});

    const performExecuteGraphWorkflow = (triggerNodeInstanceId: number) => {
        async function performEffect() {
            if (graph && validationResult && graphValid) {
                try {
                    setExecutionRunning(true);
                    setExecutionError(undefined);
                    if (flushSave) {
                        await flushSave();
                    }
                    await api.post(`/graphs/${graphId}/run`, {
                        nodeInstanceId: triggerNodeInstanceId,
                    });
                    toast('Execution started');
                    // Result will arrive via WebSocket (useLiveSync)
                    // executionRunning will be cleared by useLiveSync when result arrives
                } catch (e: any) {
                    console.error(e);
                    setExecutionRunning(false);
                    setExecutionResult(undefined);
                    setExecutionError({ message: e?.message ?? 'Failed to trigger execution' });
                }
            } else {
                setExecutionResult(undefined);
                setExecutionError(undefined);
            }
        }
        performEffect();
    };
    const performExecuteGraphWorkflowRef = useRef(performExecuteGraphWorkflow);
    performExecuteGraphWorkflowRef.current = performExecuteGraphWorkflow;

    const staticExecute = useCallback((triggerNodeInstanceId: number) => {
        performExecuteGraphWorkflowRef.current(triggerNodeInstanceId);
    }, []);

    return {
        executionResult,
        executionError,
        triggerExecution: staticExecute,
        executionRunning,
        setExecutionRunning,
        resetExecution,
        ranNodes,
    };
};
